import React, { useState } from "react";
import "./compRowCard.css";

export default function CompRowCard({ comp, isSelected, isVisible = false, onClick, isMobile, compactHorizontal }) {
  const [imageError, setImageError] = useState(false);
  const {
    image,
    title,
    price,
    mileage,
    dealer,
    city,
    state,
    distance_miles,
    priceDelta,
    priceDeltaType,
    mileageDelta,
    mileageDeltaType,
    domDelta,
    domDeltaType,
    status,
    avgPrice,
    avgDom,
    compQuality,
    qualityLevel,
    url,
    dom,
    soldDate,
    version,
    transmission,
    drivetrain
  } = comp;
  
  // Check if image is valid (not null, undefined, empty, or placeholder/watermark)
  const isValidImage = (imgUrl) => {
    if (!imgUrl || imgUrl.trim() === '') return false;
    // Check for common placeholder/watermark patterns
    const invalidPatterns = [
      'placeholder',
      'watermark',
      'via.placeholder',
      'no-image',
      'noimage',
      'default-image',
      'missing-image'
    ];
    return !invalidPatterns.some(pattern => imgUrl.toLowerCase().includes(pattern));
  };
  
  // Format sold date
  const formatSoldDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch (e) {
      return dateString;
    }
  };

  const isPositive = priceDeltaType === "under" || domDeltaType === "faster";
  const isNegative = priceDeltaType === "over" || domDeltaType === "slower";

  const isSoldComp = soldDate !== undefined && soldDate !== null;
  
  const handleClick = (e) => {
    if (isSoldComp) {
      return;
    }
    e.preventDefault();
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
    if (onClick) {
      onClick();
    }
  };

  const distanceStr = distance_miles != null
    ? (Number(distance_miles) === Math.round(distance_miles) ? `${distance_miles} mi` : `${Number(distance_miles).toFixed(1)} mi`)
    : null;
  const locationStr = [city, state].filter(Boolean).join(", ");

  if (isMobile && !isSoldComp) {
    if (compactHorizontal) {
      return (
        <div
          className="comp-row-card comp-row-card-mobile comp-row-card-compact-horizontal"
          onClick={handleClick}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleClick(e); } }}
        >
          <div className="crc-compact-thumb">
            {(!isValidImage(image) || imageError) ? (
              <div className="crc-no-image"><span>Image not available</span></div>
            ) : (
              <img src={image} alt={title} onError={() => setImageError(true)} />
            )}
          </div>
          <div className="crc-compact-content">
            <div className="crc-compact-title">{title}{version ? ` • ${version}` : ""}</div>
            <div className="crc-compact-price">{price != null && price > 0 ? `$${price.toLocaleString()}` : "—"}</div>
            <div className="crc-compact-mileage">{mileage != null ? (mileage >= 1000 ? `${(mileage / 1000).toFixed(0)}k mi` : `${mileage.toLocaleString()} mi`) : "—"}</div>
            <div className="crc-compact-dealer-row">
              <span className="crc-compact-dealer">{dealer || locationStr || "—"}</span>
              <span className="crc-compact-view">View →</span>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div
        className={`comp-row-card comp-row-card-mobile ${isSelected ? "selected" : ""}`}
        onClick={handleClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleClick(e); } }}
      >
        <div className="crc-mobile-top">
          <div className="crc-mobile-thumb">
            {(!isValidImage(image) || imageError) ? (
              <div className="crc-no-image"><span>Image not available</span></div>
            ) : (
              <img src={image} alt={title} onError={() => setImageError(true)} />
            )}
          </div>
          <div className="crc-mobile-head">
            <div className="crc-mobile-title">{title}</div>
            {version && <div className="crc-mobile-specs">{version}</div>}
            {(drivetrain || transmission) && (
              <div className="crc-mobile-drive">{[drivetrain, transmission].filter(Boolean).join(" • ")}</div>
            )}
          </div>
        </div>
        <div className="crc-mobile-details">
          <div className="crc-mobile-price-row">
            <span className="crc-mobile-price">{price != null && price > 0 ? `$${price.toLocaleString()}` : "—"}</span>
            {mileage != null && (
              <span className="crc-mobile-mileage">{mileage >= 1000 ? `${(mileage / 1000).toFixed(0)}k mi` : `${mileage.toLocaleString()} mi`}</span>
            )}
          </div>
          <div className="crc-mobile-meta">
            {[distanceStr && `${distanceStr} away`, dealer && locationStr ? `${dealer} • ${locationStr}` : dealer || locationStr].filter(Boolean).join(" • ")}
          </div>
        </div>
      </div>
    );
  }

  if (isMobile && isSoldComp) {
    if (compactHorizontal) {
      return (
        <div
          className="comp-row-card comp-row-card-mobile comp-row-card-compact-horizontal comp-row-card-compact-horizontal-sold"
          onClick={(e) => onClick && onClick()}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick && onClick(); } }}
        >
          <div className="crc-compact-thumb">
            {(!isValidImage(image) || imageError) ? (
              <div className="crc-no-image"><span>Image not available</span></div>
            ) : (
              <img src={image} alt={title} onError={() => setImageError(true)} />
            )}
          </div>
          <div className="crc-compact-content">
            <div className="crc-compact-title">{title}{version ? ` • ${version}` : ""}</div>
            <div className="crc-compact-price">{price != null && price > 0 ? `$${price.toLocaleString()}` : "—"}</div>
            <div className="crc-compact-mileage">{mileage != null ? (mileage >= 1000 ? `${(mileage / 1000).toFixed(0)}k mi` : `${mileage.toLocaleString()} mi`) : "—"}</div>
            <div className="crc-compact-sold-row">
              <span className="crc-compact-sold-meta">
                DOM: {dom != null && dom !== undefined ? `${dom} days` : "—"} • Sold: {soldDate ? formatSoldDate(soldDate) : "—"}
              </span>
            </div>
            <div className="crc-compact-dealer-row">
              <span className="crc-compact-dealer">{dealer || locationStr || "—"}</span>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div
        className={`comp-row-card comp-row-card-mobile comp-row-card-mobile-sold ${isSelected ? "selected" : ""}`}
        onClick={(e) => onClick && onClick()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick && onClick(); } }}
      >
        <div className="crc-mobile-top">
          <div className="crc-mobile-thumb">
            {(!isValidImage(image) || imageError) ? (
              <div className="crc-no-image"><span>Image not available</span></div>
            ) : (
              <img src={image} alt={title} onError={() => setImageError(true)} />
            )}
          </div>
          <div className="crc-mobile-head">
            <div className="crc-mobile-title">{title}</div>
            {version && <div className="crc-mobile-specs">{version}</div>}
            {(drivetrain || transmission) && (
              <div className="crc-mobile-drive">{[drivetrain, transmission].filter(Boolean).join(" • ")}</div>
            )}
          </div>
        </div>
        <div className="crc-mobile-details">
          <div className="crc-mobile-price-row">
            <span className="crc-mobile-price">{price != null && price > 0 ? `$${price.toLocaleString()}` : "—"}</span>
            {mileage != null && (
              <span className="crc-mobile-mileage">{mileage >= 1000 ? `${(mileage / 1000).toFixed(0)}k mi` : `${mileage.toLocaleString()} mi`}</span>
            )}
          </div>
          <div className="crc-mobile-dom">
            <span>DOM: {dom != null && dom !== undefined ? `${dom} days` : "—"}</span>
            <span className="crc-mobile-dom-sep"> • </span>
            <span>Sold: {soldDate ? formatSoldDate(soldDate) : "—"}</span>
          </div>
          <div className="crc-mobile-meta">
            {[distanceStr && `${distanceStr} away`, dealer && locationStr ? `${dealer} • ${locationStr}` : dealer || locationStr].filter(Boolean).join(" • ")}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`comp-row-card ${isSelected ? "selected" : ""} ${isSoldComp ? "sold-comp" : ""} ${isVisible ? "visible-on-map" : ""}`}
      onClick={handleClick}
    >
      {/* Thumbnail */}
      <div className="crc-thumbnail">
        {(!isValidImage(image) || imageError) ? (
          <div className="crc-no-image">
            <span>Image not available</span>
          </div>
        ) : (
          <img 
            src={image} 
            alt={title}
            onError={() => {
              setImageError(true);
            }}
          />
        )}
      </div>

      {/* Title Block */}
      <div className="crc-title-block">
        <div className="crc-title">
          {title}
          {(version || drivetrain || transmission) && (
            <span className="crc-title-version">
              {" • "}
              {[version, drivetrain, transmission].filter(Boolean).join(" • ")}
            </span>
          )}
        </div>
        <div className="crc-price-row">
          <span className="crc-price">{price != null && price > 0 ? `$${price.toLocaleString()}` : "—"}</span>
          {mileage != null ? <span className="crc-mileage">{mileage.toLocaleString()} mi</span> : <span className="crc-mileage">—</span>}
        </div>
        <div className="crc-dealer-row">
          <span className="crc-dealer">{dealer}</span>
          {(city != null || state != null) && (
            <>
              <span className="crc-dealer-sep"> • </span>
              <span className="crc-dealer-location">
                {[city, state].filter(Boolean).join(", ")}
              </span>
            </>
          )}
          {distance_miles != null && (
            <>
              <span className="crc-dealer-sep"> • </span>
              <span className="crc-dealer-distance">
                {Number(distance_miles) === Math.round(distance_miles)
                  ? `${distance_miles} mi`
                  : `${Number(distance_miles).toFixed(1)} mi`}
              </span>
            </>
          )}
        </div>
      </div>

      {/* Right Side Info - DOM and Distance (for active comps) */}
      {!isSoldComp && (dom !== undefined && dom !== null || distance_miles != null) && (
        <div className="crc-right-info">
          {dom !== undefined && dom !== null && <div className="crc-dom">DOM: {dom} days</div>}
          {distance_miles != null && (
            <div className="crc-distance">
              Distance: {Number(distance_miles) === Math.round(distance_miles)
                ? `${distance_miles} mi`
                : `${Number(distance_miles).toFixed(1)} mi`}
            </div>
          )}
        </div>
      )}

      {/* Right Side Info - DOM, Distance and Sold Date (only for sold comps) */}
      {isSoldComp && (dom !== undefined && dom !== null || distance_miles != null || soldDate) && (
        <div className="crc-right-info">
          {dom !== undefined && dom !== null && <div className="crc-dom">DOM: {dom} days</div>}
          {distance_miles != null && (
            <div className="crc-distance">
              Distance: {Number(distance_miles) === Math.round(distance_miles)
                ? `${distance_miles} mi`
                : `${Number(distance_miles).toFixed(1)} mi`}
            </div>
          )}
          {soldDate && (
            <div className="crc-sold-date">Sold Date: {formatSoldDate(soldDate)}</div>
          )}
        </div>
      )}

      {/* Chevron - only show for active comps */}
      {!isSoldComp && (
        <div className="crc-chevron">
          <span>›</span>
        </div>
      )}
    </div>
  );
}

